/**
 * Watrly SearchBox Component
 * ==========================
 * مكون صندوق البحث
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './SearchBox.css';

const SearchBox = ({
  value = '',
  onChange,
  onSearch,
  onClear,
  placeholder,
  suggestions = [],
  onSuggestionSelect,
  loading = false,
  disabled = false,
  size = 'md', // sm | md | lg
  variant = 'default', // default | filled | minimal
  showClear = true,
  autoFocus = false,
  debounceMs = 300,
  className = '',
  ...props
}) => {
  const { isRTL, t } = useTheme();
  const [internalValue, setInternalValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    setShowSuggestions(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onChange?.(newValue);
    }, debounceMs);
  };

  const handleClear = () => {
    setInternalValue('');
    onChange?.('');
    onClear?.();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        onSuggestionSelect?.(suggestions[highlightedIndex]);
        setShowSuggestions(false);
      } else {
        onSearch?.(internalValue);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    onSuggestionSelect?.(suggestion);
    setShowSuggestions(false);
    setInternalValue(suggestion.label || suggestion);
  };

  const classes = [
    'watrly-search',
    `watrly-search--${size}`,
    `watrly-search--${variant}`,
    disabled && 'watrly-search--disabled',
    loading && 'watrly-search--loading',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} dir={isRTL ? 'rtl' : 'ltr'} {...props}>
      <div className="watrly-search__wrapper">
        <span className="watrly-search__icon">
          {loading ? (
            <svg className="watrly-search__spinner" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          )}
        </span>

        <input
          ref={inputRef}
          type="text"
          value={internalValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={placeholder || t?.('general.search') || 'بحث...'}
          disabled={disabled}
          className="watrly-search__input"
          autoComplete="off"
        />

        {showClear && internalValue && !loading && (
          <button
            type="button"
            className="watrly-search__clear"
            onClick={handleClear}
            aria-label={t?.('general.clear') || 'مسح'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="watrly-search__suggestions">
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id || index}
              className={[
                'watrly-search__suggestion',
                index === highlightedIndex && 'watrly-search__suggestion--highlighted',
              ].filter(Boolean).join(' ')}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion.icon && (
                <span className="watrly-search__suggestion-icon">
                  {suggestion.icon}
                </span>
              )}
              <span className="watrly-search__suggestion-label">
                {suggestion.label || suggestion}
              </span>
              {suggestion.description && (
                <span className="watrly-search__suggestion-desc">
                  {suggestion.description}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBox;
